/**
 * PatientCabinetPage — ЛК ПАЦИЕНТА.
 * Структура 1:1 с UX-демо «Cortivox — финал 25.10» (cortivox-demo.html, вид «Пациент»):
 *   шапка (приветствие, дата, серия, выход) → пилюли-разделы (Главное / Тренажёры /
 *   План (ИПР) / Отчёты / Мои врачи / Личные данные) → контент max-w-3xl → футер.
 * ВАЖНО: пилюля «Тренажёры» ведёт на страницу Каталога упражнений (/exercises).
 * «Продолжить тренировку» открывает следующее ПО ПОРЯДКУ упражнение из ИПР;
 * «Сейчас» и клик по упражнению в плане открывают именно это упражнение.
 * Оверлеи: чат с врачом, смена почты.
 * Данные: ../lib/cortivoxApi (реальные), ../auth/AuthContext.
 * Единственная адаптация под проект: классы cvx-* питаются css-файлом
 * src/cortivox-cvx.css (не трогает лендинг и tailwind.config).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Gamepad2, ClipboardList, FileText, Stethoscope, User,
  Flame, LogOut, Loader2, Play, ChevronRight, ArrowLeft,
  CheckCircle2, Sparkles, Search, X, Send, MessageCircle,
  AlertCircle, Mail, Cake, GraduationCap, AtSign, KeyRound,
  ShieldCheck, Info, Calendar, Clock3, TrendingUp, TrendingDown,
  Zap, Brain, Shapes, Calculator, Hand, Pencil, Smile, BrainCircuit,
} from 'lucide-react';
import {
  getMyProfile, getLinks, acceptLink, rejectLink, getCvIpr,
  listCvTraces, getCvTrace, listTrainerReports,
} from '../lib/cortivoxApi';
import { useAuth } from '../auth/AuthContext';
import { exercisesCatalog } from '../exercises-data';
import { SessionReportExpand } from './SessionReport';
import { PatientCardView } from './DoctorCabinetPage';

// >>> CVX COLORS AUTO-BLOCK (не редактировать вручную) >>>
// String.raw — ОБЯЗАТЕЛЬНО: иначе JS в template literal съедает обратные слэши
// в селекторах (hover\:, \/40) и правила вариантов молча умирают.
// ЕДИНЫЙ блок для ВСЕХ страниц (кабинет врача, кабинет пациента, каталог,
// отчёт): какой бы файл ни загрузился первым — набор цветов одинаковый.
const CVX_COLORS_CSS = String.raw`
/* ==========================================================================
   cortivox-cvx.css — цвета кабинетов Cortivox (пациент и врач).
   Готово по демо UX «финал 25.10». НЕ трогает лендинг: это отдельные
   классы bg-cvx-*/text-cvx-*/..., которые нигде больше в проекте не встречаются.
   Подключение: в src/main.jsx после import "./index.css" добавить
     import "./cortivox-cvx.css";
   ========================================================================== */

.bg-cvx-accent{ background-color: #CFEFE0; }
.bg-cvx-accent\/10{ background-color: #CFEFE01A; }
.bg-cvx-background{ background-color: #F5FBF8; }
.bg-cvx-background\/95{ background-color: #F5FBF8F2; }
.bg-cvx-border{ background-color: #D8E2DE; }
.bg-cvx-card{ background-color: #FFFFFF; }
.bg-cvx-card\/60{ background-color: #FFFFFF99; }
.bg-cvx-muted{ background-color: #EDF4F1; }
.bg-cvx-muted-foreground\/50{ background-color: #576C6C80; }
.bg-cvx-popover{ background-color: #FFFFFF; }
.bg-cvx-primary{ background-color: #216858; }
.bg-cvx-primary-foreground\/20{ background-color: #F7FBF933; }
.bg-cvx-primary\/10{ background-color: #2168581A; }
.bg-cvx-primary\/25{ background-color: #21685840; }
.bg-cvx-primary\/40{ background-color: #21685866; }
.bg-cvx-primary\/80{ background-color: #216858CC; }
.bg-cvx-secondary{ background-color: #E8F2EE; }
.bg-cvx-secondary\/20{ background-color: #E8F2EE33; }
.bg-cvx-secondary\/30{ background-color: #E8F2EE4D; }
.bg-cvx-secondary\/40{ background-color: #E8F2EE66; }
.bg-cvx-secondary\/50{ background-color: #E8F2EE80; }
.bg-cvx-secondary\/60{ background-color: #E8F2EE99; }
.bg-cvx-sidebar{ background-color: #F0F9F6; }
.border-cvx-border{ border-color: #D8E2DE; }
.border-cvx-input{ border-color: #D8E2DE; }
.border-cvx-primary{ border-color: #216858; }
.divide-cvx-border{ border-color: #D8E2DE; }
.focus\:border-cvx-primary\/50:focus{ border-color: #21685880; }
.focus\:ring-cvx-ring:focus{ --tw-ring-color: #508E7E; }
.focus\:ring-cvx-ring\/20:focus{ --tw-ring-color: #508E7E33; }
.hover\:bg-cvx-accent:hover{ background-color: #CFEFE0; }
.hover\:bg-cvx-primary\/90:hover{ background-color: #216858E6; }
.hover\:bg-cvx-secondary:hover{ background-color: #E8F2EE; }
.hover\:bg-cvx-secondary\/50:hover{ background-color: #E8F2EE80; }
.hover\:bg-cvx-sidebar-accent:hover{ background-color: #CFEFE0; }
.hover\:border-cvx-destructive\/40:hover{ border-color: #DC262666; }
.hover\:border-cvx-primary\/40:hover{ border-color: #21685866; }
.hover\:border-cvx-primary\/50:hover{ border-color: #21685880; }
.hover\:text-cvx-destructive:hover{ color: #DC2626; }
.hover\:text-cvx-primary:hover{ color: #216858; }
.placeholder\:text-cvx-muted-foreground::placeholder{ color: #576C6C; }
.placeholder\:text-cvx-muted-foreground\/50::placeholder{ color: #576C6C80; }
.placeholder\:text-cvx-muted-foreground\/70::placeholder{ color: #576C6CB3; }
.ring-cvx-primary\/30{ --tw-ring-color: #2168584D; }
.stroke-cvx-primary{ stroke: #216858; }
.stroke-cvx-secondary{ stroke: #E8F2EE; }
.text-cvx-accent-foreground{ color: #094E3E; }
.text-cvx-destructive{ color: #DC2626; }
.text-cvx-foreground{ color: #182729; }
.text-cvx-muted-foreground{ color: #576C6C; }
.text-cvx-muted-foreground\/40{ color: #576C6C66; }
.text-cvx-muted-foreground\/60{ color: #576C6C99; }
.text-cvx-muted-foreground\/80{ color: #576C6CCC; }
.text-cvx-primary{ color: #216858; }
.text-cvx-primary-foreground{ color: #F7FBF9; }
.text-cvx-primary-foreground\/70{ color: #F7FBF9B3; }
.text-cvx-primary\/60{ color: #21685899; }
.text-cvx-secondary-foreground{ color: #18433A; }

/* Правки 26.10 — унификация с лендингом: чёрные заголовки, янтарное
   «Требуют внимания», логотип Cortivox.ai (как на лендинге) */
.bg-cvx-warn{ background-color: #FEF6E3; }
.bg-cvx-warn-card{ background-color: #FFFCF2; }
.border-cvx-warn{ border-color: #EFDFAE; }
.text-cvx-warn{ color: #B45309; }
.text-cvx-logo-ai{ color: #508E7E; }

/* Правки 26.10 v2 — как на лендинге: чёрный — для действий и активных
   состояний (кнопки, выбранные вкладки, логотип), зелёный — только акценты.
   ink = основной чёрный лендинга (shadcn neutral --primary, oklch 0.145 0 0) */
.bg-cvx-ink{ background-color: #0A0A0A; }
.bg-cvx-ink\/40{ background-color: #0A0A0A66; }
.hover\:bg-cvx-ink\/90:hover{ background-color: #0A0A0AE6; }
.text-cvx-ink{ color: #0A0A0A; }
.border-cvx-ink{ border-color: #0A0A0A; }
.hover\:text-cvx-ink:hover{ color: #0A0A0A; }

/* Единая кнопка «Назад» (везде одинаковая: отчёты, карточка пациента, чат) */
.cvx-back{ display:inline-flex; align-items:center; gap:.5rem; height:2.5rem; padding:0 1rem; border-radius:9999px; border:1px solid #D8E2DE; background:#FFFFFF; color:#0A0A0A; font-size:.875rem; font-weight:600; line-height:1; white-space:nowrap; flex:none; cursor:pointer; transition:background-color .15s ease, border-color .15s ease; }
.cvx-back:hover{ background:#E8F2EE; border-color:#C9DAD3; }
.cvx-back:disabled{ opacity:.5; }
.divide-cvx-border > :not([hidden]) ~ :not([hidden]) { border-color: #D8E2DE; }

/* Кастомные классы демо (не Tailwind) */
.cv-shadow { box-shadow: 0 1px 2px #10362f0a, 0 4px 16px #10362f0d; }
.cv-large { font-size: 1.125rem; }
.cv-large .cv-big-btn { border-radius: 1.25rem; height: 4rem; font-size: 1.1875rem; }
.cv-scroll::-webkit-scrollbar { width: 8px; }
.cv-scroll::-webkit-scrollbar-thumb { background: #d7e8e1; border-radius: 8px; }
`;
if (typeof document !== 'undefined') {
  // Версионная защита (26.10, v4): старый блок #cvx-colors (без версии = v0,
  // например из старого файла каталога) ЗАМЕНЯЕТСЯ этим блоком (v5). Раньше guard
  // «уже есть — пропустить» оставлял старые цвета навсегда, и чёрные кнопки/
  // логотип становились прозрачными, а белый текст — невидимым.
  const __cvxPrev = document.getElementById('cvx-colors');
  const __cvxPrevVer = __cvxPrev ? (Number(__cvxPrev.getAttribute('data-cvx-version') || '0') || 0) : 0;
  if (!__cvxPrev || __cvxPrevVer < 5) {
    if (__cvxPrev) __cvxPrev.parentNode.removeChild(__cvxPrev);
    const __cvxStyle = document.createElement('style');
    __cvxStyle.id = 'cvx-colors';
    __cvxStyle.setAttribute('data-cvx-version', '5');
    __cvxStyle.textContent = CVX_COLORS_CSS;
    document.head.appendChild(__cvxStyle);
  }
}
// <<< CVX COLORS AUTO-BLOCK <<<

// ─────────────────────────── Каталог упражнений (единый источник) ───────────────────────────
// Данные — ../exercises-data (тот же файл, что в Каталоге /exercises).
// Сам каталог с модулями живёт на странице /exercises (пилюля «Тренажёры» ведёт туда);
// здесь — только хелперы для плана (ИПР) и отчётов.

const CAT = Array.isArray(exercisesCatalog) ? exercisesCatalog : [];
const byId = Object.fromEntries(CAT.map((e) => [String(e.id), e]));
const titleOf = (slug) => byId[String(slug)]?.title
  ?? (String(slug).match(/^\d+$/) ? `Упражнение ${slug}` : String(slug));
const domainLabelOf = (slug) => byId[String(slug)]?.domain ?? 'Тренажёр';
const minutesOf = (slug) => byId[String(slug)]?.minutes ?? 10;
const descOf = (slug) => byId[String(slug)]?.description ?? '';
const domainIcon = { 'Речь': MessageCircle, 'Когнитивные навыки': Brain, 'Связная речь': MessageCircle, 'Мимика': Smile, 'Моторика': Hand };
const IconOf = (slug) => domainIcon[byId[String(slug)]?.domain] ?? Pencil;

// ─────────────────────────── Мелкие хелперы ───────────────────────────

const plural = (n, one, few, many) => {
  const a = n % 10, b = n % 100;
  if (a === 1 && b !== 11) return one;
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return few;
  return many;
};
const initials = (v = '') => v.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
const lastActiveText = (d) =>
  d == null ? 'ещё не занимался' : d === 0 ? 'занимался сегодня' : d === 1 ? 'занимался вчера' : `не занимался ${d} ${plural(d, 'день', 'дня', 'дней')}`;
const fmtDateTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
// scoredOn — только дата (YYYY-MM-DD), без времени
const fmtDate = (iso) => {
  const s = String(iso || '');
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};
const ageFrom = (birthDate) => {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a -= 1;
  return a;
};
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const birthRu = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return !y || !m || !d ? iso : `${d} ${MONTHS[m - 1]} ${y}`;
};
const eduLabel = (v) => (v == null ? null : v >= 15 ? 'Высшее' : v >= 12 ? 'Среднее специальное' : v >= 9 ? 'Среднее' : 'Начальное');
const todayLong = () => {
  try { return new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }); } catch { return ''; }
};

// ─────────────────────────── Кольцо прогресса дня ───────────────────────────

function ProgressRing({ value, total, size = 112, stroke = 12, className = '' }) {
  const frac = total > 0 ? Math.min(value / total, 1) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }} role="img" aria-label={`Выполнено ${value} из ${total}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-cvx-secondary" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
          className="stroke-cvx-primary transition-[stroke-dashoffset] duration-700"
          strokeDasharray={c} strokeDashoffset={c * (1 - frac)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold leading-none text-cvx-foreground">{value}</span>
        <span className="mt-0.5 text-xs text-cvx-muted-foreground">из {total}</span>
      </div>
    </div>
  );
}

// ─────────────────────────── График активности за неделю ───────────────────────────

function WeekChart({ data, className = '' }) {
  const days = Array.isArray(data) && data.length === 7
    ? data
    : ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((l) => ({ label: l, minutes: 0 }));
  const max = Math.max(...days.map((d) => d.minutes), 1);
  return (
    <div className={`flex items-end justify-between gap-2 ${className}`} role="img" aria-label="Активность за неделю в минутах">
      {days.map((d) => (
        <div key={d.label} className="flex flex-1 flex-col items-center gap-1.5">
          <div className="flex h-16 w-full items-end justify-center">
            <div
              className={`w-full max-w-[1.5rem] rounded-t-[1rem] transition-all ${d.minutes === 0 ? 'bg-cvx-secondary' : 'bg-cvx-primary/80'}`}
              style={{ height: `${Math.max((d.minutes / max) * 100, 6)}%` }}
              title={`${d.minutes} мин`}
            />
          </div>
          <span className="text-[11px] leading-none text-cvx-muted-foreground">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── Статус связи ───────────────────────────

function StatusPill({ status = 'active', direction }) {
  if (status === 'active')
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[oklch(0.93_0.05_150)] px-2.5 py-1 text-xs font-semibold text-[oklch(0.4_0.1_150)]">
        <span className="size-1.5 rounded-full bg-[oklch(0.6_0.15_150)]" /> Активен
      </span>
    );
  if (status === 'pending')
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[oklch(0.95_0.06_90)] px-2.5 py-1 text-xs font-semibold text-[oklch(0.5_0.1_75)]">
        <span className="size-1.5 rounded-full bg-[oklch(0.75_0.14_80)]" />
        {direction === 'incoming' ? 'Ждёт вашего ответа' : 'Запрос отправлен'}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-cvx-muted px-2.5 py-1 text-xs font-semibold text-cvx-muted-foreground">
      <span className="size-1.5 rounded-full bg-cvx-muted-foreground/50" /> В архиве
    </span>
  );
}

// ─────────────────────────── Чат с врачом ───────────────────────────
// TODO(API): сообщений нет в API — переписка хранится в памяти до перезагрузки.

function ChatOverlay({ doctor, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = () => {
    const v = text.trim();
    if (!v) return;
    const time = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    setMessages((m) => [...m, { id: `p${Date.now()}`, from: 'patient', text: v, time }]);
    setText('');
    // TODO(API): отправка сообщения врачу (пока хранится локально).
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-cvx-background">
      <header className="flex items-center gap-3 border-b border-cvx-border bg-cvx-card px-4 py-3.5">
        <button onClick={onClose} className="cvx-back" aria-label="Вернуться к врачам">
          <ArrowLeft className="size-4" /> Назад
        </button>
        <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-cvx-accent text-lg font-bold text-cvx-accent-foreground">
          {initials(doctor.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold leading-tight text-cvx-foreground">{doctor.name}</p>
          <p className="truncate text-sm text-cvx-muted-foreground">{doctor.specialty} · отвечает в течение дня</p>
        </div>
      </header>
      <div ref={listRef} className="cv-scroll flex-1 overflow-y-auto px-5 py-5" role="log" aria-label="Переписка с врачом">
        <div className="mx-auto flex max-w-2xl flex-col gap-3">
          <p className="mb-1 text-center text-sm text-cvx-muted-foreground">Переписка видна только вам и врачу</p>
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center">
              <MessageCircle className="size-8 text-cvx-muted-foreground" />
              <p className="text-lg text-cvx-muted-foreground">Напишите первое сообщение — врач ответит в течение дня.</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.from === 'patient' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-3xl px-5 py-3.5 text-lg leading-snug ${m.from === 'patient' ? 'rounded-br-[1rem] bg-cvx-primary text-cvx-primary-foreground' : 'rounded-bl-[1rem] border border-cvx-border bg-cvx-card text-cvx-foreground'}`}>
                <p>{m.text}</p>
                <p className={`mt-1 text-sm ${m.from === 'patient' ? 'text-cvx-primary-foreground/70' : 'text-cvx-muted-foreground'}`}>{m.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-cvx-border bg-cvx-card px-4 py-3.5">
        <div className="mx-auto flex max-w-2xl items-center gap-2.5">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Напишите сообщение…"
            aria-label="Текст сообщения"
            className="h-14 min-w-0 flex-1 rounded-2xl border border-cvx-input bg-cvx-background px-5 text-lg text-cvx-foreground placeholder:text-cvx-muted-foreground focus:outline-none focus:ring-2 focus:ring-cvx-ring"
          />
          <button
            onClick={send}
            disabled={!text.trim()}
            className="inline-flex size-14 shrink-0 items-center justify-center rounded-2xl bg-cvx-primary text-cvx-primary-foreground transition-all hover:bg-cvx-primary/90 disabled:opacity-40"
            aria-label="Отправить сообщение"
          >
            <Send className="size-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Модал смены почты ───────────────────────────
// TODO(API): подтверждения почты нет в API — шаг кода принимает любые 6 цифр (как в демо).

function EmailModal({ current, onDone, onClose }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const submitEmail = () => {
    const v = email.trim();
    if (!emailRe.test(v)) { setError('Проверьте адрес — кажется, в нём опечатка.'); return; }
    if (v.toLowerCase() === String(current || '').toLowerCase()) { setError('Это ваш текущий адрес. Введите новый.'); return; }
    setError('');
    setStep('code');
  };
  const submitCode = () => {
    if (code.trim().length !== 6) { setError('Введите 6 цифр из письма.'); return; }
    setError('');
    setStep('done');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-cvx-primary/40 sm:items-center sm:p-6" onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-label={step === 'done' ? 'Почта обновлена' : 'Смена почты'}
        className="cv-shadow w-full max-w-md rounded-t-3xl border border-cvx-border bg-cvx-card p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {step !== 'done' && (
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-cvx-foreground">Смена почты</h2>
              <p className="mt-1 text-sm text-cvx-muted-foreground">Текущий адрес: {current || '—'}</p>
            </div>
            <button onClick={onClose} className="rounded-[1.25rem] p-2 text-cvx-muted-foreground transition-colors hover:bg-cvx-secondary" aria-label="Закрыть">
              <X className="size-6" />
            </button>
          </div>
        )}

        {step === 'email' && (
          <div className="mt-5 flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="font-semibold text-cvx-foreground">Новая электронная почта</span>
              <input
                type="email" value={email} autoFocus
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="anna@newmail.ru"
                className="h-14 rounded-2xl border border-cvx-input bg-cvx-background px-4 text-lg text-cvx-foreground placeholder:text-cvx-muted-foreground focus:outline-none focus:ring-2 focus:ring-cvx-ring"
              />
            </label>
            {error && (
              <p className="flex items-center gap-2 text-sm font-medium text-cvx-destructive">
                <AlertCircle className="size-4 shrink-0" /> {error}
              </p>
            )}
            <p className="text-sm text-cvx-muted-foreground">
              Пришлём код подтверждения. Старый адрес продолжит работать, пока вы не подтвердите новый.
            </p>
            <button
              onClick={submitEmail} disabled={!email.trim()}
              className="cv-big-btn inline-flex items-center justify-center gap-3 rounded-2xl bg-cvx-primary px-8 font-bold text-cvx-primary-foreground shadow transition-all hover:bg-cvx-primary/90 disabled:opacity-40"
            >
              Получить код
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="mt-5 flex flex-col gap-4">
            <span className="flex size-16 items-center justify-center self-center rounded-full bg-cvx-accent text-cvx-accent-foreground">
              <Mail className="size-8" />
            </span>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-cvx-foreground">Введите код</h2>
              <p className="mt-1 text-sm text-cvx-muted-foreground">Отправили 6-значный код на {email.trim()}</p>
            </div>
            <input
              inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="······"
              className="h-16 rounded-2xl border border-cvx-input bg-cvx-background text-center text-2xl font-bold tracking-[0.45em] text-cvx-foreground placeholder:text-cvx-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-cvx-ring"
            />
            {error && (
              <p className="flex items-center justify-center gap-2 text-sm font-medium text-cvx-destructive">
                <AlertCircle className="size-4 shrink-0" /> {error}
              </p>
            )}
            <p className="text-center text-sm text-cvx-muted-foreground">Прототип: подойдёт любой код из 6 цифр</p>
            <button
              onClick={submitCode} disabled={code.length !== 6}
              className="cv-big-btn inline-flex items-center justify-center rounded-2xl bg-cvx-primary font-bold text-cvx-primary-foreground shadow transition-all hover:bg-cvx-primary/90 disabled:opacity-40"
            >
              Подтвердить
            </button>
            <button onClick={() => { setStep('email'); setError(''); }} className="text-center text-sm font-semibold text-cvx-muted-foreground transition-colors hover:text-cvx-primary">
              ← Изменить адрес
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center gap-4 py-2 text-center">
            <span className="flex size-20 items-center justify-center rounded-full bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]">
              <CheckCircle2 className="size-10" />
            </span>
            <div>
              <h2 className="text-2xl font-bold text-cvx-foreground">Почта обновлена</h2>
              <p className="mt-1.5 text-base text-cvx-muted-foreground">
                Теперь вход по адресу <span className="font-semibold text-cvx-foreground">{email.trim()}</span>
              </p>
            </div>
            <button
              onClick={() => onDone(email.trim())}
              className="cv-big-btn inline-flex w-full items-center justify-center rounded-2xl bg-cvx-primary font-bold text-cvx-primary-foreground shadow transition-colors hover:bg-cvx-primary/90"
            >
              Готово
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Отчёты пациента ───────────────────────────

// Отчёты двух типов (НЕ смешиваются: сначала голосовой тренажёр, потом видео):
//  1) «Речевая беглость» — голосовой тренажёр (GET /api/trainers): засчитанные
//     слова считает сервер (правила MoCA), здесь только показываем;
//  2) видео-упражнения (артикуляция/мимика) — cv_traces с видеоразбором.
// В таблице тренажёров рядом живут результаты «Зрительного восприятия»
// (stepId 'v01'..'v20' ПО КАРТОЧКЕ — каждая показывается 2 занятия подряд,
// algorithm_version visual-web-1) — карточка различает их по stepId. Поля
// читаем и с плоского объекта, и из scoreResult — работает при любом
// маппинге в cortivoxApi.

const VISUAL_STEP_RE = /^v\d+$/; // 'v01'..'v20' — «Зрительное восприятие»
const RETELL_STEP_RE = /^r\d+$/; // 'r01'..'r04' — «Пересказ близко к тексту»
const SCHULTE_STEP_RE = /^s\d+$/; // 's01'..'s20' — «Таблицы Шульте»

const fmtElapsedCard = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function TrainerReportCard({ r }) {
  const sr = r?.scoreResult || {};
  const isVisual = VISUAL_STEP_RE.test(String(r?.stepId || ''));
  const count = Number(r?.count ?? sr.count ?? 0);
  const counted = Array.isArray(r?.counted) ? r.counted
    : (Array.isArray(sr.counted) ? sr.counted : []);

  if (isVisual) {
    const total = Number(r?.total ?? sr.total ?? 0);
    const matched = Array.isArray(r?.matched) ? r.matched
      : (Array.isArray(sr.matched) ? sr.matched : counted);
    const missed = Array.isArray(r?.missed) ? r.missed
      : (Array.isArray(sr.missed) ? sr.missed : []);
    const errors = Array.isArray(r?.errors) ? r.errors
      : (Array.isArray(sr.errors) ? sr.errors : []);
    const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
    const thinkingMs = r?.thinkingMs ?? sr.thinkingMs; // «на раздумье»
    // Номер в stepId теперь — КАРТОЧКА (1..20), она показывается 2 занятия:
    // карточка 5 → дни 9–10.
    const cardNum = parseInt(String(r.stepId).slice(1), 10) || 0;
    return (
      <li className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-bold text-cvx-foreground">Зрительное восприятие</p>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
              <Calendar className="size-4" /> {fmtDate(r.scoredOn)}
              <span className="rounded-[0.875rem] bg-cvx-secondary px-2.5 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                карточка {cardNum} · дни {cardNum * 2 - 1}–{cardNum * 2}
              </span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
              {count}<span className="text-lg font-semibold text-cvx-muted-foreground">/{total}</span>
            </p>
            <p className="mt-1 text-xs text-cvx-muted-foreground">
              названо верно
              {elapsedMs != null ? ` · общее ${fmtElapsedCard(elapsedMs)}` : ''}
              {thinkingMs != null ? ` · раздумье ${fmtElapsedCard(thinkingMs)}` : ''}
            </p>
          </div>
        </div>
        {matched.length > 0 && (
          <p className="mt-3 rounded-2xl bg-cvx-muted p-3.5 text-sm leading-relaxed text-cvx-foreground">
            {matched.join(', ')}
          </p>
        )}
        {missed.length > 0 && (
          <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">
            Не названо: {missed.join(', ')}
          </p>
        )}
        {errors.length > 0 && (
          <p className="mt-1 text-xs leading-relaxed text-cvx-muted-foreground">
            Лишние слова ({errors.length}): {errors.join(', ')}
          </p>
        )}
      </li>
    );
  }

  if (RETELL_STEP_RE.test(String(r?.stepId || ''))) {
    // «Пересказ близко к тексту»: percent по ядерным вехам, блоки, флаги цепочек.
    const percent = Number(sr.percent ?? 0);
    const coreClosed = Number(sr.coreClosed ?? 0);
    const coreTotal = Number(sr.coreTotal ?? 0);
    const detailClosed = Number(sr.detailClosed ?? 0);
    const detailTotal = Number(sr.detailTotal ?? 0);
    const blocks = Array.isArray(sr.blocks) ? sr.blocks : [];
    const flags = Array.isArray(sr.flags) ? sr.flags : [];
    const partialNums = Array.isArray(sr.partialNums) ? sr.partialNums : [];
    const storyTitle = r?.config?.title || sr.title || '';
    const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
    return (
      <li className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-bold text-cvx-foreground">Пересказ близко к тексту</p>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
              <Calendar className="size-4" /> {fmtDate(r.scoredOn)}
              {storyTitle && (
                <span className="rounded-[0.875rem] bg-cvx-secondary px-2.5 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                  «{storyTitle}»
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
              {percent}<span className="text-lg font-semibold text-cvx-muted-foreground">%</span>
            </p>
            <p className="mt-1 text-xs text-cvx-muted-foreground">
              ядро {coreClosed}/{coreTotal} вех
              {detailTotal > 0 ? ` · детали ${detailClosed}/${detailTotal}` : ''}
              {elapsedMs != null ? ` · ${fmtElapsedCard(elapsedMs)}` : ''}
              {` · подсказок ${Number(sr.hintsUsed) || 0}`}
            </p>
          </div>
        </div>
        {blocks.length > 0 && (
          <p className="mt-3 text-xs leading-relaxed text-cvx-muted-foreground">
            Блоки: {blocks.map((b) => `${b.name} ${b.coreClosed}/${b.coreTotal}`).join(' · ')}
          </p>
        )}
        {partialNums.length > 0 && (
          <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">
            Частично (сверить по стенограмме): вехи {partialNums.join(', ')}
          </p>
        )}
        {flags.map((f) => (
          <p key={f.type} className="mt-2 text-xs font-semibold leading-relaxed text-amber-700">
            Флаг «{f.label}». {f.message}
          </p>
        ))}
      </li>
    );
  }

  if (SCHULTE_STEP_RE.test(String(r?.stepId || ''))) {
  const tableNum = parseInt(String(r.stepId).slice(1), 10) || 0;
  const errors = Number(r?.errors ?? sr.errors ?? 0);
  const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
  const done = r?.completed ?? sr.completed;
  return (
    <li className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-bold text-cvx-foreground">Таблицы Шульте</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
            <Calendar className="size-4" /> {fmtDate(r.scoredOn)}
            <span className="rounded-[0.875rem] bg-cvx-secondary px-2.5 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
              таблица {tableNum} · дни {tableNum * 2 - 1}–{tableNum * 2}
            </span>
            {done === false && ' · не завершено'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
            {fmtElapsedCard(elapsedMs)}
          </p>
          <p className="mt-1 text-xs text-cvx-muted-foreground">
            время · найдено {count}/49 · ошибок {errors}
          </p>
        </div>
      </div>
    </li>
  );
}

  return (
    <li className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-bold text-cvx-foreground">Речевая беглость</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
            <Calendar className="size-4" /> {fmtDate(r.scoredOn)}
            {r.stepId && (
              <span className="rounded-[0.875rem] bg-cvx-secondary px-2.5 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                блок {r.stepId}
              </span>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">{count}</p>
          <p className="mt-1 text-xs text-cvx-muted-foreground">{plural(count, 'слово', 'слова', 'слов')} засчитано</p>
        </div>
      </div>
      {counted.length > 0 && (
        <p className="mt-3 rounded-2xl bg-cvx-muted p-3.5 text-sm leading-relaxed text-cvx-foreground">
          {counted.join(', ')}
        </p>
      )}
    </li>
  );
}

function PatientReports({ patientId }) {
  const [cvRows, setCvRows] = useState(null);           // видео-упражнения (артикуляция)
  const [trainerRows, setTrainerRows] = useState(null); // «Речевая беглость»
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setCvRows(null); setTrainerRows(null); setErr('');
    (async () => {
      try {
        // Лёгкий список (без trace — он весит мегабайты); полный разбор — по кнопке.
        const list = (await listCvTraces({ patientId }) || []).slice(0, 12);
        if (alive) setCvRows(list);
      } catch (e) {
        if (!alive) return;
        setErr(e?.status === 404
          ? 'Отчёты недоступны: сервер ещё не обновлён (нужен новый cv_reports.py и рестарт API)'
          : e?.message || 'Не удалось получить список тренировок');
        setCvRows([]);
      }
      // «Речевая беглость»: свой кабинет — patientId не передаём, сервер берёт
      // пользователя из сессии (так же делает сама страница тренажёра).
      try {
        const res = await listTrainerReports({ limit: 200 });
        const items = (Array.isArray(res) ? res : res?.items || [])
          .slice()
          .sort((a, b) => String(b?.scoredOn || '').localeCompare(String(a?.scoredOn || '')))
          .slice(0, 12);
        if (alive) setTrainerRows(items);
      } catch { if (alive) setTrainerRows([]); }
    })();
    return () => { alive = false; };
  }, [patientId]);

  if (!Array.isArray(cvRows) || !Array.isArray(trainerRows))
    return (
      <p className="flex items-center gap-2 text-lg text-cvx-muted-foreground">
        <Loader2 className="size-5 animate-spin" /> Загрузка отчётов…
      </p>
    );

  if (cvRows.length === 0 && trainerRows.length === 0)
    return (
      <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-14 text-center">
        <FileText className="size-8 text-cvx-muted-foreground" />
        {err
          ? <p className="max-w-md text-lg text-cvx-destructive">{err}</p>
          : <p className="text-lg text-cvx-muted-foreground">Отчёт появится после первой тренировки</p>}
      </div>
    );

  return (
    <div className="flex flex-col gap-3">
      {trainerRows.length > 0 && (
        <>
          <h2 className="mt-1 text-sm font-bold uppercase tracking-wider text-cvx-muted-foreground">Речевая беглость</h2>
          <ul className="flex flex-col gap-3">
            {trainerRows.map((r) => (
              <TrainerReportCard key={r.id || `${r.stepId}-${r.scoredOn}`} r={r} />
            ))}
          </ul>
        </>
      )}
      {cvRows.length > 0 && (
        <>
          <h2 className="mt-3 text-sm font-bold uppercase tracking-wider text-cvx-muted-foreground">Видео-упражнения</h2>
          <ul className="flex flex-col gap-3">
            {cvRows.map((r) => (
              <li key={r.id} className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-lg font-bold text-cvx-foreground">{titleOf(r.exerciseSlug)}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
                      <Calendar className="size-4" /> {fmtDateTime(r.createdAt)}
                      {typeof r.frames === 'number' && (
                        <span className="rounded-[0.875rem] bg-cvx-secondary px-2.5 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                          запись: {r.frames} кадров{r.truncated ? ' (обрезана)' : ''}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {/* Разбор сессии — 5 согласованных секций (утренняя версия):
                    заключение, «было → стало», кривая с порогами, таблица, детализация. */}
                <div className="mt-3">
                  <SessionReportExpand row={r} sessions={cvRows} exerciseTitle={titleOf(r.exerciseSlug)} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Личные данные ───────────────────────────

function ProfileTab({ profile, email, age, edu, onEditEmail }) {
  const name = profile.name || profile.full_name || profile.email || 'Пациент';
  return (
    <>
      <h1 className="text-2xl font-bold text-cvx-ink">Личные данные</h1>
      <p className="-mt-3 text-lg text-cvx-muted-foreground">Редко нужная информация — собрана здесь, чтобы не мешала на главном экране.</p>
      <section className="cv-shadow flex flex-col gap-4 rounded-3xl border border-cvx-border bg-cvx-card p-6">
        <div className="flex items-center gap-4">
          <span className="flex size-16 shrink-0 items-center justify-center rounded-3xl bg-cvx-accent text-2xl font-bold text-cvx-accent-foreground">
            {initials(name)}
          </span>
          <div>
            <p className="text-xl font-bold text-cvx-foreground">{name}</p>
            <p className="text-sm text-cvx-muted-foreground">Пациент платформы Cortivox</p>
          </div>
        </div>
        <ul className="divide-y divide-cvx-border">
          {age != null && (
            <li className="flex items-center gap-4 py-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-cvx-accent text-cvx-accent-foreground">
                <Cake className="size-6" />
              </span>
              <span>
                <span className="block text-sm text-cvx-muted-foreground">Дата рождения</span>
                <span className="block text-lg font-semibold text-cvx-foreground">
                  {profile.birth_date ? `${birthRu(profile.birth_date)} · ${age} ${plural(age, 'год', 'года', 'лет')}` : `${age} ${plural(age, 'год', 'года', 'лет')}`}
                </span>
              </span>
            </li>
          )}
          {edu != null && (
            <li className="flex items-center gap-4 py-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-cvx-accent text-cvx-accent-foreground">
                <GraduationCap className="size-6" />
              </span>
              <span>
                <span className="block text-sm text-cvx-muted-foreground">Образование</span>
                <span className="block text-lg font-semibold text-cvx-foreground">{eduLabel(edu)}</span>
                <span className="block text-sm text-cvx-muted-foreground">Нужно врачу, чтобы верно оценить результаты тестов</span>
              </span>
            </li>
          )}
          {email && (
            <li className="flex items-center gap-4 py-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-cvx-accent text-cvx-accent-foreground">
                <AtSign className="size-6" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-cvx-muted-foreground">Электронная почта</span>
                <span className="block truncate text-lg font-semibold text-cvx-foreground">{email}</span>
                <span className="block text-sm text-cvx-muted-foreground">Используется для входа и уведомлений</span>
              </span>
            </li>
          )}
        </ul>
        <button
          onClick={onEditEmail}
          className="inline-flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl border border-cvx-primary px-7 font-bold text-cvx-primary transition-colors hover:bg-cvx-accent sm:w-auto sm:self-start"
        >
          <KeyRound className="size-5" /> Изменить почту
        </button>
      </section>
      <section className="flex items-start gap-3.5 rounded-3xl border border-cvx-border bg-cvx-secondary/50 p-5">
        <ShieldCheck className="mt-0.5 size-6 shrink-0 text-cvx-primary" />
        <p className="text-sm leading-relaxed text-cvx-secondary-foreground">
          Данные защищены. Возраст и образование видит только ваш лечащий врач — они помогают верно толковать результаты
          шкал (например, MoCA учитывает образование). Почта нужна для входа в платформу и не передаётся третьим лицам.
        </p>
      </section>
    </>
  );
}

// ─────────────────────────── Разделы кабинета ───────────────────────────

const NAV = [
  { key: 'home', label: 'Главное', icon: Home },
  { key: 'exercises', label: 'Тренажёры', icon: Gamepad2 },
  { key: 'plan', label: 'План (ИПР)', icon: ClipboardList },
  { key: 'reports', label: 'Отчёты', icon: FileText },
  { key: 'doctors', label: 'Мои врачи', icon: Stethoscope },
  { key: 'profile', label: 'Личные данные', icon: User },
];

// ─────────────────────────── Главная страница ЛК пациента ───────────────────────────

/**
 * Точка входа: демо открывает КАРТОЧКУ ПАЦИЕНТА по /patient с state.patientProfile
 * (из карточки врача). Если state нет — обычный ЛК пациента.
 */
export default function PatientCabinetPage() {
  const location = useLocation();
  const navigate = useNavigate();
  if (location.state?.patientProfile) {
    return (
      <main className="min-h-screen bg-cvx-background text-cvx-foreground">
        <PatientCardView patient={location.state.patientProfile} onBack={() => navigate('/doctor')} />
      </main>
    );
  }
  return <PatientCabinetInner />;
}

function PatientCabinetInner() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('home');
  const [plan, setPlan] = useState(null);           // [{slug, done, isNext}] — порядок задаёт ИПР
  const [week, setWeek] = useState(null);           // [{label, minutes}]
  const [lastTrained, setLastTrained] = useState(null); // slug последней тренировки (из отчётов)
  const [chatDoctor, setChatDoctor] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [email, setEmail] = useState('');

  const reloadLinks = useCallback(async () => setLinks(await getLinks().catch(() => [])), []);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMyProfile();
        if (me?.role === 'doctor') { navigate('/doctor', { replace: true }); return; }
        setProfile(me);
        setEmail(me?.email || '');
        setLinks(await getLinks().catch(() => []));
      } catch { setProfile(null); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const patientId = profile?.id;

  useEffect(() => {
    if (!patientId) return;
    let alive = true;
    // План (ИПР): порядок задаёт врач. Слаг → заголовок каталога.
    (async () => {
      try {
        const data = await getCvIpr(patientId);
        const slugs = (Array.isArray(data) ? data : data?.items || [])
          .map((it) => (typeof it === 'string' ? it : it.exerciseSlug)).filter(Boolean);
        const next = slugs.map((s) => ({ slug: s, done: false, isNext: false }));
        if (next.length) next[0].isNext = true;
        if (alive) setPlan(next);
      } catch { if (alive) setPlan([]); }
      // Активность за неделю: TODO(API) — пока нули, как «нет данных».
      setWeek(['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((label) => ({ label, minutes: 0 })));
    })();
    return () => { alive = false; };
  }, [patientId]);

  // «Продолжить тренировку»: следующее ПО ПОРЯДКУ упражнение из ИПР —
  // после последней тренированной (по отчётам). Данных нет — начинаем с первой.
  useEffect(() => {
    if (!patientId) return;
    let alive = true;
    listCvTraces({ patientId })
      .then((rows) => {
        const r0 = (rows || []).find((x) => x?.exerciseSlug);
        if (alive) setLastTrained(r0?.exerciseSlug ?? null);
      })
      .catch(() => { /* отчётов нет или сервер старый — просто начнём с первой */ });
    return () => { alive = false; };
  }, [patientId]);

  const doneCount = plan ? plan.filter((p) => p.done).length : 0;
  // следующее по порядку: после последней тренированной; нет данных — первая
  const nextItem = (() => {
    if (!plan || plan.length === 0) return null;
    if (!lastTrained) return plan[0];
    const i = plan.findIndex((p) => p.slug === lastTrained);
    return plan[(i + 1) % plan.length]; // i === -1 → plan[0]
  })();
  const totalMinutes = Array.isArray(week) ? week.reduce((s, d) => s + (d.minutes || 0), 0) : null;

  const signOutAll = async () => {
    await signOut();
    try { localStorage.clear(); } catch { /* noop */ }
    navigate('/');
  };

  const act = async (fn, arg) => {
    setBusy(true);
    setError('');
    try { await fn(arg); await reloadLinks(); } catch (e) { setError(e.message || 'Не удалось выполнить действие'); }
    finally { setBusy(false); }
  };

  if (loading)
    return (
      <main className="flex min-h-screen items-center justify-center bg-cvx-background text-cvx-muted-foreground">
        Загрузка профиля…
      </main>
    );

  if (!profile)
    return (
      <main className="flex min-h-screen items-center justify-center bg-cvx-background p-6">
        <div className="flex max-w-sm flex-col gap-3 text-center">
          <p className="text-sm text-cvx-primary">Войдите, чтобы открыть кабинет пациента.</p>
          <Link to="/login" className="text-sm font-semibold text-cvx-primary underline">Войти или зарегистрироваться</Link>
        </div>
      </main>
    );

  // Пересказ живёт на своей странице /retell (слаг 'retell' из каталога);
  // r01..r04 — на будущее, если захочешь назначать конкретные рассказы.
  const openExercise = (slug) => {
    const s = String(slug);
    if (s === 'retell') { navigate('/retell'); return; }
    if (RETELL_STEP_RE.test(s)) {
      const n = parseInt(s.slice(1), 10) || 1;
      navigate(`/retell?story=t${String(n).padStart(2, '0')}`);
      return;
    }
    navigate(`/exercise/${slug}`);
  };

  
  const firstName = (profile.name || profile.full_name || profile.email || '').split(' ')[0];
  const firstLetter = (firstName[0] || 'П').toUpperCase();
  const streak = profile.streak ?? null;
  const myLinks = links.filter((l) => l.status === 'active' || l.status === 'pending');
  const activeDoctor = links.find((l) => l.status === 'active');
  const nameOf = (l) => l.counterpart?.full_name || l.counterpart?.email || l.doctor_id;
  const isIncoming = (l) => l?.status === 'pending' && l?.direction === 'incoming';

  return (
    <div className="cv-large min-h-screen bg-cvx-background">
      {chatDoctor && <ChatOverlay doctor={chatDoctor} onClose={() => setChatDoctor(null)} />}
      {emailModal && (
        <EmailModal current={email} onDone={(v) => { setEmail(v); setEmailModal(false); }} onClose={() => setEmailModal(false)} />
      )}

      {/* Шапка */}
      <header className="sticky top-0 z-30 border-b border-cvx-border bg-cvx-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <span className="flex size-12 items-center justify-center rounded-2xl bg-cvx-primary text-xl font-bold text-cvx-primary-foreground">
              {firstLetter}
            </span>
            <div>
              <p className="font-bold leading-tight text-cvx-foreground">Здравствуйте, {firstName}!</p>
              <p className="text-sm text-cvx-muted-foreground">{todayLong()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {streak != null && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[oklch(0.96_0.045_70)] px-3.5 py-2 font-bold text-[oklch(0.5_0.13_60)]" title="Дней занятий подряд">
                <Flame className="size-5" /> {streak}
              </span>
            )}
            <button
              onClick={signOutAll}
              className="rounded-[1.25rem] p-2.5 text-cvx-muted-foreground transition-colors hover:bg-cvx-secondary hover:text-cvx-destructive"
              aria-label="Выйти"
            >
              <LogOut className="size-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Пилюли-разделы */}
      <nav className="mx-auto max-w-3xl px-5 pt-4" aria-label="Разделы кабинета">
        <div className="cv-scroll flex gap-2 overflow-x-auto pb-1">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => {
                // «Тренажёры» НЕ вкладка, а переход: ведёт в Каталог упражнений (/exercises).
                if (key === 'exercises') { navigate('/exercises'); return; }
                setTab(key);
              }}
              aria-current={tab === key ? 'page' : undefined}
              className={`inline-flex shrink-0 items-center gap-2 rounded-2xl px-5 py-3.5 font-semibold transition-colors ${tab === key ? 'bg-cvx-primary text-cvx-primary-foreground shadow' : 'border border-cvx-border bg-cvx-card text-cvx-secondary-foreground hover:bg-cvx-secondary'}`}
            >
              <Icon className="size-5" /> {label}
            </button>
          ))}
        </div>
      </nav>

      <main className="mx-auto flex max-w-3xl flex-col gap-5 px-5 py-5">
        {/* Главное */}
        {tab === 'home' && (
          <>
            <section className="cv-shadow flex flex-col gap-6 rounded-3xl border border-cvx-border bg-cvx-card p-6 sm:flex-row sm:items-center">
              {plan && plan.length > 0 && <ProgressRing value={doneCount} total={plan.length} size={112} stroke={12} />}
              <div className="flex-1">
                {plan && plan.length > 0 ? (
                  <>
                    <h1 className="text-2xl font-bold leading-tight text-cvx-ink">
                      {doneCount === plan.length ? 'План на сегодня выполнен!' : `Осталось ${plan.length - doneCount} ${plural(plan.length - doneCount, 'упражнение', 'упражнения', 'упражнений')}`}
                    </h1>
                    <p className="mt-1.5 text-lg text-cvx-muted-foreground">
                      {doneCount === plan.length
                        ? 'Отличная работа — займитесь завтра. Серия сохраняется.'
                        : nextItem ? `Следующее: ${titleOf(nextItem.slug)} · ≈${minutesOf(nextItem.slug)} мин` : 'Хорошего дня!'}
                    </p>
                    {nextItem ? (
                      <button
                        onClick={() => openExercise(nextItem.slug)}
                        className="cv-big-btn mt-4 inline-flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-cvx-primary text-lg font-bold text-cvx-primary-foreground shadow transition-colors hover:bg-cvx-primary/90 sm:w-auto sm:px-10"
                      >
                        <Play className="size-6" /> Продолжить тренировку
                      </button>
                    ) : (
                      <p className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-[oklch(0.93_0.05_150)] px-6 py-4 font-bold text-[oklch(0.4_0.1_150)]">
                        <Sparkles className="size-5" /> Серия продлена: {(streak ?? 0) + 1} {plural((streak ?? 0) + 1, 'день', 'дня', 'дней')} подряд
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold leading-tight text-cvx-ink">{plan ? 'План пока пуст' : 'Загружаем ваш план…'}</h1>
                    <p className="mt-1.5 text-lg text-cvx-muted-foreground">
                      {plan ? 'Врач составит индивидуальный план — упражнения появятся здесь. Сами упражнения — в разделе «Тренажёры»: отметьте нужные галочкой «Включить в ИПР».' : 'Проверяем упражнения от вашего врача.'}
                    </p>
                    {plan && (
                      <button
                        onClick={() => navigate('/exercises')}
                        className="mt-4 inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-cvx-primary px-8 text-lg font-bold text-cvx-primary-foreground shadow transition-colors hover:bg-cvx-primary/90"
                      >
                        <Gamepad2 className="size-5" /> Открыть тренажёры
                      </button>
                    )}
                  </>
                )}
              </div>
            </section>

            {plan && plan.length > 0 && (
              <section>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-cvx-foreground">План на сегодня</h2>
                  <span className="text-sm font-semibold text-cvx-muted-foreground">{doneCount} из {plan.length} готово</span>
                </div>
                <ul className="flex flex-col gap-2.5">
                  {plan.map((p) => {
                    const Icon = IconOf(p.slug);
                    return (
                      <li key={p.slug}>
                        <button
                          onClick={() => !p.done && openExercise(p.slug)}
                          disabled={p.done}
                          className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all ${p.done
                            ? 'border-cvx-border bg-cvx-secondary/40 opacity-70'
                            : p.isNext
                              ? 'border-cvx-primary bg-cvx-card shadow ring-1 ring-cvx-primary/30 hover:-translate-y-0.5'
                              : 'border-cvx-border bg-cvx-card hover:-translate-y-0.5 hover:border-cvx-primary/40'}`}
                        >
                          <span className={`flex size-[3.25rem] shrink-0 items-center justify-center rounded-2xl ${p.done ? 'bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]' : 'bg-cvx-accent text-cvx-accent-foreground'}`}>
                            {p.done ? <CheckCircle2 className="size-6" /> : <Icon className="size-6" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block truncate text-lg font-bold ${p.done ? 'text-cvx-muted-foreground line-through' : 'text-cvx-foreground'}`}>
                              {titleOf(p.slug)}
                            </span>
                            <span className="mt-0.5 block text-sm text-cvx-muted-foreground">
                              {domainLabelOf(p.slug)} · ≈{minutesOf(p.slug)} мин
                            </span>
                          </span>
                          {p.isNext && !p.done && (
                            <span className="shrink-0 rounded-full bg-cvx-primary px-3.5 py-1.5 text-sm font-bold text-cvx-primary-foreground">Сейчас</span>
                          )}
                          {!p.done && <ChevronRight className="size-5 shrink-0 text-cvx-muted-foreground" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 px-1 text-sm text-cvx-muted-foreground">Порядок задал ваш врач — выполняйте по очереди. Нажатие на упражнение открывает его.</p>
              </section>
            )}

            <section className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-cvx-foreground">Мой прогресс за неделю</h2>
                <span className="font-bold text-cvx-foreground">{totalMinutes != null ? `${totalMinutes} мин` : '— мин'}</span>
              </div>
              <WeekChart className="mt-4" data={week} />
            </section>
          </>
        )}

        {/* «Тренажёры» — не вкладка: пилюля ведёт в Каталог упражнений (/exercises) */}

        {/* План (ИПР) */}
        {tab === 'plan' && (
          <>
            <h1 className="text-2xl font-bold text-cvx-ink">Мой план реабилитации</h1>
            <p className="-mt-3 text-lg text-cvx-muted-foreground">
              Составил врач: {activeDoctor ? nameOf(activeDoctor) : 'ваш врач'}. Выполняйте по порядку — план обновляется автоматически.
            </p>
            {plan == null ? (
              <p className="flex items-center gap-2 text-lg text-cvx-muted-foreground">
                <Loader2 className="size-5 animate-spin" /> Загрузка плана…
              </p>
            ) : plan.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-14 text-center">
                <ClipboardList className="size-8 text-cvx-muted-foreground" />
                <p className="text-lg text-cvx-muted-foreground">Врач ещё не добавил упражнения в ваш план. Они появятся здесь.</p>
              </div>
            ) : (
              <ol className="flex flex-col gap-2.5">
                {plan.map((p, i) => (
                  <li key={p.slug} className={`flex items-center gap-4 rounded-2xl border border-cvx-border bg-cvx-card p-4 ${p.done ? 'opacity-70' : ''}`}>
                    <span className={`flex size-10 shrink-0 items-center justify-center rounded-[1.25rem] font-bold ${p.done ? 'bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]' : 'bg-cvx-secondary text-cvx-secondary-foreground'}`}>
                      {p.done ? <CheckCircle2 className="size-5" /> : i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-lg font-bold ${p.done ? 'text-cvx-muted-foreground line-through' : 'text-cvx-foreground'}`}>
                        {titleOf(p.slug)}
                      </span>
                      <span className="text-sm text-cvx-muted-foreground">{domainLabelOf(p.slug)} · ≈{minutesOf(p.slug)} мин</span>
                    </span>
                    {!p.done && (
                      <button
                        onClick={() => openExercise(p.slug)}
                        className="inline-flex shrink-0 items-center gap-2 rounded-[1.25rem] bg-cvx-primary px-5 py-3 font-semibold text-cvx-primary-foreground hover:bg-cvx-primary/90"
                      >
                        <Play className="size-5" /> Начать
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}

        {/* Отчёты */}
        {tab === 'reports' && (
          <>
            <h1 className="text-2xl font-bold text-cvx-ink">Мои отчёты</h1>
            <p className="-mt-3 text-lg text-cvx-muted-foreground">Как прошли ваши тренировки. Врач видит эти же данные.</p>
            <PatientReports patientId={patientId} />
          </>
        )}

        {/* Личные данные */}
        {tab === 'profile' && (
          <ProfileTab
            profile={profile}
            email={email}
            age={ageFrom(profile.birth_date)}
            edu={profile.educationYears ?? profile.education_years}
            onEditEmail={() => setEmailModal(true)}
          />
        )}

        {/* Мои врачи */}
        {tab === 'doctors' && (
          <>
            <h1 className="text-2xl font-bold text-cvx-ink">Мои врачи</h1>
            <p className="-mt-3 text-lg text-cvx-muted-foreground">Ваши результаты и план доступны только подключённым врачам.</p>
            <ul className="flex flex-col gap-3">
              {myLinks.length === 0 && (
                <p className="rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center text-lg text-cvx-muted-foreground">
                  Пока никто не привязан. Врач отправит запрос по вашему email — он появится здесь.
                </p>
              )}
              {myLinks.map((l) => {
                const n = nameOf(l);
                return (
                  <li key={l.id} className="cv-shadow flex flex-wrap items-center gap-4 rounded-3xl border border-cvx-border bg-cvx-card p-5">
                    <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-cvx-accent text-xl font-bold text-cvx-accent-foreground">
                      {initials(n)}
                    </span>
                    <div className="min-w-40 flex-1">
                      <p className="text-lg font-bold text-cvx-foreground">{n}</p>
                      <p className="text-sm text-cvx-muted-foreground">{l.counterpart?.specialty || 'Врач'}</p>
                    </div>
                    <StatusPill status={l.status} direction={isIncoming(l) ? 'incoming' : 'outgoing'} />
                    {l.status === 'active' && (
                      <button
                        onClick={() => setChatDoctor({ name: n, specialty: l.counterpart?.specialty || 'Врач', linkId: l.id })}
                        className="inline-flex items-center gap-2 rounded-[1.25rem] bg-cvx-primary px-5 py-3 font-semibold text-cvx-primary-foreground transition-colors hover:bg-cvx-primary/90"
                      >
                        <MessageCircle className="size-5" /> Написать
                      </button>
                    )}
                    {l.status === 'pending' && isIncoming(l) && (
                      <div className="flex w-full gap-2 sm:w-auto">
                        <button
                          onClick={() => act(acceptLink, l.id)}
                          disabled={busy}
                          className="flex-1 rounded-[1.25rem] bg-cvx-primary px-5 py-3 font-semibold text-cvx-primary-foreground hover:bg-cvx-primary/90 disabled:opacity-60 sm:flex-none"
                        >
                          Принять
                        </button>
                        <button
                          onClick={() => act(rejectLink, l.id)}
                          disabled={busy}
                          className="rounded-[1.25rem] border border-cvx-border px-5 py-3 font-semibold text-cvx-primary hover:bg-cvx-secondary disabled:opacity-60"
                        >
                          Отклонить
                        </button>
                      </div>
                    )}
                    {l.status === 'pending' && !isIncoming(l) && (
                      <span className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
                        <Loader2 className="size-3.5 animate-spin" /> Ожидает вашего подтверждения
                      </span>
                    )}
                  </li>
                );
              })}
              {error && <p className="text-sm text-cvx-destructive">{error}</p>}
            </ul>
          </>
        )}
      </main>

      <footer className="mx-auto max-w-3xl px-5 pb-10 pt-2">
        {/* Логотип как на лендинге (Cortivox.ai) — «телереабилитация» убрана по просьбе врача */}
        <div className="flex flex-col items-center gap-2.5">
          <span className="flex items-center gap-2.5">
            <span style={{ backgroundColor: '#0A0A0A' }} className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white"><BrainCircuit className="size-4" /></span>
            <span style={{ color: '#0A0A0A' }} className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">Cortivox<span className="text-cvx-logo-ai">.ai</span></span>
          </span>
          <p className="text-xs text-cvx-muted-foreground">© 2026 Cortivox.ai</p>
        </div>
      </footer>
    </div>
  );
}
